import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Repeat, Trash2, Pause, Play } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

export default function RecurringOrdersPanel() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", category: "", frequency_days: 7, auction_hours: 12,
    delivery_address: "", direct_supplier_id: "",
    items: [{ name: "Merce settimanale", quantity: 1, unit: "kg", notes: "" }],
  });

  const load = () => api.get("/recurring").then(r => setItems(r.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/categories").then(r => setCats(r.data));
    api.get("/restaurant/favorites").then(r => setFavorites(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    if (!form.title || !form.category || !form.delivery_address) {
      toast.error("Compila titolo, categoria, indirizzo");
      return;
    }
    setSaving(true);
    try {
      await api.post("/recurring", form);
      toast.success("Ordine ricorrente attivato");
      setOpen(false);
      setForm({ ...form, title: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (id) => {
    try { await api.patch(`/recurring/${id}/toggle`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo ordine ricorrente?")) return;
    try { await api.delete(`/recurring/${id}`); load(); toast.success("Eliminato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">Crea ordini che si ripetono automaticamente ogni N giorni.</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="ga-btn-primary rounded-full" data-testid="recurring-create-button"><Plus className="w-4 h-4 mr-2" />Nuovo ricorrente</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Ordine ricorrente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Titolo</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Consegna settimanale carne" data-testid="rec-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Categoria</Label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="rec-cat-select"><SelectValue placeholder="Categoria" /></SelectTrigger>
                    <SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ogni (giorni)</Label>
                  <Input type="number" min={1} max={90} value={form.frequency_days} onChange={e => setForm({ ...form, frequency_days: Number(e.target.value) })} data-testid="rec-freq-input" />
                </div>
              </div>
              <div>
                <Label>Indirizzo consegna</Label>
                <Input value={form.delivery_address} onChange={e => setForm({ ...form, delivery_address: e.target.value })} placeholder="Via, Città" data-testid="rec-address-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Durata asta (ore)</Label>
                  <Input type="number" min={1} max={12} value={form.auction_hours} onChange={e => setForm({ ...form, auction_hours: Number(e.target.value) })} data-testid="rec-hours-input" />
                </div>
                <div>
                  <Label>Fornitore diretto (opz.)</Label>
                  <Select value={form.direct_supplier_id || "none"} onValueChange={v => setForm({ ...form, direct_supplier_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="rec-supplier-select"><SelectValue placeholder="Asta pubblica" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Asta pubblica</SelectItem>
                      {favorites.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Nota articoli (semplificata)</Label>
                <Input value={form.items[0].name} onChange={e => setForm({ ...form, items: [{ ...form.items[0], name: e.target.value }] })} placeholder="Merce settimanale" data-testid="rec-items-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} className="ga-btn-primary rounded-full" data-testid="rec-save-button">{saving ? "Salvataggio..." : "Attiva"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 && (
        <div className="ga-card p-8 text-center text-sm text-slate-500" data-testid="rec-empty">
          Nessun ordine ricorrente. Crea il tuo primo per non dimenticare mai una consegna.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map(it => (
          <div key={it.id} className={`ga-card p-5 ${it.enabled ? "" : "opacity-60"}`} data-testid={`rec-item-${it.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display text-lg font-bold text-slate-900 truncate flex items-center gap-2">
                  <Repeat className="w-4 h-4 text-orange-500" /> {it.title}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-1">{it.category} · ogni {it.frequency_days} giorni</div>
                <div className="text-xs text-slate-600 mt-2">Prossimo: <span className="font-mono">{it.next_run_at ? new Date(it.next_run_at).toLocaleString("it-IT") : "-"}</span></div>
                <div className="text-xs text-slate-500 mt-1">Eseguiti: {it.runs_count}</div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => toggle(it.id)} className="p-2 rounded-lg hover:bg-slate-100" data-testid={`rec-toggle-${it.id}`}>
                  {it.enabled ? <Pause className="w-4 h-4 text-amber-600" /> : <Play className="w-4 h-4 text-emerald-600" />}
                </button>
                <button onClick={() => remove(it.id)} className="p-2 rounded-lg hover:bg-red-50" data-testid={`rec-remove-${it.id}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
